import { isBoolean } from 'lodash/fp';
import { useMutation, useQueryClient } from 'react-query';
import { useDispatch } from 'react-redux';

import { useAuth } from '@portals/redux';
import { toastrError, toastrSuccess } from '@portals/redux/actions/toastr';
import { OrganizationPricingPlanFeatures, TenantType } from '@portals/types';

import {
  CONFIGURATION_API_URL,
  configurationQueryKeys,
} from './configuration.constants';
import { ConfigType, NotificationSettingsType } from './configuration.types';
import { useApiQuery } from '../../hooks';
import { QueryOptions, ServerError } from '../../types';
import { fetchApiRequest, useRequestOptions } from '../../utils';

export const useConfig = (queryOptions: QueryOptions<ConfigType> = {}) => {
  const auth = useAuth();

  return useApiQuery<ConfigType>(
    CONFIGURATION_API_URL,
    configurationQueryKeys.config,
    {
      ...queryOptions,
      enabled:
        (!isBoolean(queryOptions.enabled) || queryOptions.enabled) &&
        Boolean(auth),
    }
  );
};

export function useRequestAccountDeletion() {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const { url, options } = useRequestOptions({
    url: `${CONFIGURATION_API_URL}/request_account_deletion`,
    method: 'POST',
  });

  return useMutation<void, ServerError, void>({
    mutationFn: () => {
      return fetchApiRequest(url, options);
    },
    onSuccess: () => {
      dispatch(toastrSuccess('Account deletion requested'));

      queryClient.invalidateQueries(configurationQueryKeys.config);
    },
    onError: ({ error }: { error: string }) => {
      dispatch(toastrError(error));
    },
  });
}

export function useUpdateNotifications() {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const { url, options } = useRequestOptions({
    url: `${CONFIGURATION_API_URL}/notifications`,
    method: 'POST',
  });

  return useMutation<void, ServerError, NotificationSettingsType>({
    mutationFn: (notifications) => {
      return fetchApiRequest(url, {
        ...options,
        body: JSON.stringify({
          notifications,
        }),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(configurationQueryKeys.config);
    },
    onError: ({ error }: { error: string }) => {
      dispatch(toastrError(error));
    },
  });
}

interface UseUpdateConfigurationParams {
  updatedConfig: Partial<{ logo_url: string }>;
  withSuccessNotification?: boolean;
}

export const useUpdateConfiguration = () => {
  const dispatch = useDispatch();

  const queryClient = useQueryClient();
  const { url, options } = useRequestOptions({
    url: CONFIGURATION_API_URL,
    method: 'PUT',
  });

  return useMutation<ConfigType, ServerError, UseUpdateConfigurationParams>({
    mutationFn: ({ updatedConfig }) => {
      return fetchApiRequest(url, {
        ...options,
        body: JSON.stringify(updatedConfig),
      });
    },
    onSuccess: (config, { withSuccessNotification }) => {
      queryClient.invalidateQueries(configurationQueryKeys.config);

      if (withSuccessNotification) {
        dispatch(toastrSuccess('Configuration settings updated successfully'));
      }
    },
    onError: ({ error }) => dispatch(toastrError(error)),
  });
};

export function useSendReport() {
  const dispatch = useDispatch();

  const { url, options } = useRequestOptions({
    url: `${CONFIGURATION_API_URL}/send_report`,
    method: 'POST',
  });

  return useMutation<void, ServerError, string>({
    mutationFn: (email) => {
      return fetchApiRequest(url, {
        ...options,
        body: JSON.stringify(email),
      });
    },
    onSuccess: () => {
      dispatch(toastrSuccess('Report sent successfully'));
    },
    onError: ({ error }: { error: string }) => {
      dispatch(toastrError(error));
    },
  });
}

// Helpers
export function useOrganizationConfig() {
  const config = useConfig();

  return config.data?.[TenantType.Organization];
}

export function useSettingsConfig() {
  const config = useConfig();

  return config.data?.settings;
}

export function useDesign() {
  const organizationConfig = useOrganizationConfig();

  return organizationConfig?.design;
}

export function useFeatureFlags() {
  const { data } = useConfig();

  return data?.feature_flags;
}

export function usePortalCapabilities() {
  const config = useConfig();

  return config.data?.portal_capabilities;
}

export function useWithSupportCenter() {
  const portalCapabilities = usePortalCapabilities();

  return Boolean(portalCapabilities?.support_center);
}

export function useSpaceDashboard() {
  const config = useConfig();

  return config.data?.space_dashboard;
}

export function usePricingPlanFeatures() {
  const organizationConfig = useOrganizationConfig();

  return organizationConfig?.pricing_plan?.features;
}

export function usePricingPlanFeature(
  feature: keyof OrganizationPricingPlanFeatures
) {
  const pricingPlanFeatures = usePricingPlanFeatures();

  return pricingPlanFeatures?.[feature];
}
